import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Download, FileImage, FileText, Loader2 } from 'lucide-react';
import { FaDownload } from 'react-icons/fa';
import { ExportService, type ExportOptions } from '../lib/export/exportService';
import type { Canvas as FabricCanvas } from 'fabric';

interface ExportButtonProps {
  canvas: FabricCanvas | null;
  currentQuestionId?: string;
  currentQuestionOrder?: number;
  isEnabled?: boolean; // Controlled by allowExport prop
  className?: string;
  showText?: boolean; // Force text display
}

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (options: ExportOptions) => void;
  isExporting: boolean;
}

const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose, onExport, isExporting }) => {
  const [format, setFormat] = useState<'png' | 'jpeg' | 'pdf'>('png');
  const [quality, setQuality] = useState(90);
  const [includeBackground, setIncludeBackground] = useState(true);
  const [filename, setFilename] = useState('');
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    // Check if dark mode is enabled
    const checkDarkMode = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };
    
    checkDarkMode();
    
    // Listen for theme changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { 
      attributes: true, 
      attributeFilter: ['class'] 
    });
    
    return () => observer.disconnect();
  }, []);

  if (!isOpen) return null;

  const handleExport = () => {
    onExport({
      format,
      quality: quality / 100,
      includeQuestionBackground: includeBackground,
      filename: filename || undefined
    });
  };

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-[9999]" onClick={onClose} />
      
      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-[10000] pointer-events-none">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-6 w-[90vw] max-w-md max-h-[80vh] overflow-y-auto pointer-events-auto">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Dışa Aktar</h2>

        {/* Format Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Format Seçin
          </label>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setFormat('png')}
              className={`flex flex-col items-center p-3 border rounded-lg transition-colors ${
                format === 'png'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                  : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
              }`}
            >
              <FileImage className="w-6 h-6 mb-1" />
              <span className="text-sm">PNG</span>
            </button>
            <button
              onClick={() => setFormat('jpeg')}
              className={`flex flex-col items-center p-3 border rounded-lg transition-colors ${
                format === 'jpeg'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                  : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
              }`}
            >
              <FileImage className="w-6 h-6 mb-1" />
              <span className="text-sm">JPEG</span>
            </button>
            <button
              onClick={() => setFormat('pdf')}
              className={`flex flex-col items-center p-3 border rounded-lg transition-colors ${
                format === 'pdf'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                  : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
              }`}
            >
              <FileText className="w-6 h-6 mb-1" />
              <span className="text-sm">PDF</span>
            </button>
          </div>
        </div>

        {/* Quality Slider (for JPEG) */}
        {format === 'jpeg' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Kalite: {quality}%
            </label>
            <input
              type="range"
              min="10"
              max="100"
              step="10"
              value={quality}
              onChange={(e) => setQuality(Number(e.target.value))}
              className="w-full accent-blue-500 dark:accent-blue-400"
            />
          </div>
        )}

        {/* Include Background Option */}
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={includeBackground}
              onChange={(e) => setIncludeBackground(e.target.checked)}
              className="mr-2 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Soru arka planını dahil et
            </span>
          </label>
        </div>

        {/* Filename Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Dosya Adı (isteğe bağlı)
          </label>
          <input
            type="text"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            placeholder="tahta-export"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isExporting}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
          >
            İptal
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Dışa Aktarılıyor...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Dışa Aktar
              </>
            )}
          </button>
        </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export const ExportButton: React.FC<ExportButtonProps> = ({
  canvas,
  currentQuestionId,
  currentQuestionOrder,
  isEnabled = false,
  className = '',
  showText = false
}) => {
  const [showModal, setShowModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Don't render if export is not allowed
  if (!isEnabled) {
    return null;
  }

  const handleExport = async (options: ExportOptions) => {
    if (!canvas) {
      console.error('Canvas not available');
      return;
    }

    setIsExporting(true);
    
    try {
      // Add question info to filename if available
      if (!options.filename && currentQuestionOrder) {
        options.filename = `tahta-soru-${currentQuestionOrder}`;
      }

      await ExportService.exportCanvas(canvas, options);
      setShowModal(false);
    } catch (error: any) {
      console.error('Export failed:', error);
      
      // Show user-friendly error message
      let errorMessage = 'Dışa aktarma işlemi başarısız oldu. ';
      
      if (error?.name === 'SecurityError' || error?.message?.includes('tainted') || error?.message?.includes('Tainted')) {
        errorMessage = 'Soru görselleri CORS politikası nedeniyle dışa aktarılamıyor. "Soru arka planını dahil et" seçeneğini kapatarak tekrar deneyebilirsiniz.';
      } else {
        errorMessage += error?.message || 'Lütfen tekrar deneyin.';
      }
      
      alert(errorMessage);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={className || `inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors`}
        title="Dışa Aktar"
      >
        {className && !showText ? (
          <FaDownload size={20} />
        ) : (
          <>
            {className ? <FaDownload size={20} /> : <Download size={20} />}
            <span className="text-sm">Dışa Aktar</span>
          </>
        )}
      </button>

      <ExportModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onExport={handleExport}
        isExporting={isExporting}
      />
    </>
  );
};